import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LineChart } from '@mui/x-charts/LineChart';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { getAuthHeaders } from '../../../utils/authUtils';
import { createApiUrl } from '../../../utils/apiConfig';
import { useSnackbar } from '../../CentralSnackbar';
import { useFormatConfig, formatDateSync } from '../../../utils/dateTimeFormat';

const Addenda = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const { isLoaded: isFormatConfigLoaded } = useFormatConfig();
  const chartRef = useRef(null);
  
  // State
  const [addendaData, setAddendaData] = useState(null);
  const [addendaLoading, setAddendaLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('month'); // Default: Monat
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  
  // Nur eine Kurve für Addenda
  const [showAddenda, setShowAddenda] = useState(true);

  // Expose download functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handlePdfDownload,
    handleCsvDownload
  }));

  // Load Addenda data on mount
  useEffect(() => {
    loadAddendaData();
  }, []);

  // Shift + Mausrad zum Wechseln der Zeiträume
  useEffect(() => {
    const handleWheel = (event) => {
      if (event.shiftKey) {
        event.preventDefault();
        
        const timeFilters = ['date', 'week', 'month', 'quarter', 'year'];
        const currentIndex = timeFilters.indexOf(timeFilter);
        
        let newIndex;
        if (event.deltaY < 0) {
          newIndex = (currentIndex + 1) % timeFilters.length;
        } else {
          newIndex = (currentIndex - 1 + timeFilters.length) % timeFilters.length;
        }
        
        setTimeFilter(timeFilters[newIndex]);
        setStartPeriod('');
        setEndPeriod('');
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [timeFilter]);

  const loadAddendaData = async () => {
    setAddendaLoading(true);
    try {
      const response = await fetch(createApiUrl('statistics/personal/addenda'), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAddendaData(data.addenda);
      } else {
        showError(t('statistics.personal.addenda.loadError', 'Fehler beim Laden der Addenda'), 'ERR-STATS-ADD-001');
      }
    } catch (error) {
      console.error('Error loading Addenda data:', error);
      showError(t('statistics.personal.addenda.loadError', 'Fehler beim Laden der Addenda'), 'ERR-STATS-ADD-002');
    } finally {
      setAddendaLoading(false);
    }
  };

  const handleTimeFilterChange = (event) => {
    setTimeFilter(event.target.value);
    setStartPeriod('');
    setEndPeriod('');
  };

  const handleStartPeriodChange = (event) => {
    setStartPeriod(event.target.value);
  };

  const handleEndPeriodChange = (event) => {
    setEndPeriod(event.target.value);
  };

  // CSV Export Function
  const handleCsvDownload = () => {
    if (!addendaData || !showAddenda) return;

    const formatPeriod = (period) => {
      if (!period) return period;
      try {
        switch (timeFilter) {
          case 'date':
            if (!isFormatConfigLoaded) return period;
            return formatDateSync(period);
          case 'week':
            const weekMatch = period.match(/(\d{4})-W(\d{2})/);
            if (weekMatch) return `KW${weekMatch[2]} - ${weekMatch[1]}`;
            return period;
          case 'month':
            const monthMatch = period.match(/(\d{4})-(\d{2})/);
            if (monthMatch) return `${monthMatch[2]}.${monthMatch[1]}`;
            return period;
          case 'quarter':
            const quarterMatch = period.match(/(\d{4})-Q(\d)/);
            if (quarterMatch) return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
            return period;
          case 'year':
            return period;
          default:
            return period;
        }
      } catch (error) {
        return period;
      }
    };

    // Get chart data (reuse the logic from the component)
    let data = [];
    switch (timeFilter) {
      case 'date':
        data = addendaData.by_date || [];
        break;
      case 'week':
        data = addendaData.by_week || [];
        break;
      case 'month':
        data = addendaData.by_month || [];
        break;
      case 'quarter':
        data = addendaData.by_quarter || [];
        break;
      case 'year':
        data = addendaData.by_year || [];
        break;
      default:
        data = addendaData.by_month || [];
    }

    const chartData = data.map(item => ({
      period: item.period,
      addendaCount: item.count || 0
    }));

    // Apply period filtering
    let filteredData = chartData;
    if (startPeriod && endPeriod) {
      const startIdx = chartData.findIndex(d => d.period === startPeriod);
      const endIdx = chartData.findIndex(d => d.period === endPeriod);
      if (startIdx !== -1 && endIdx !== -1) {
        filteredData = chartData.slice(startIdx, endIdx + 1);
      }
    } else if (startPeriod) {
      const startIdx = chartData.findIndex(d => d.period === startPeriod);
      if (startIdx !== -1) {
        filteredData = chartData.slice(startIdx);
      }
    } else if (endPeriod) {
      const endIdx = chartData.findIndex(d => d.period === endPeriod);
      if (endIdx !== -1) {
        filteredData = chartData.slice(0, endIdx + 1);
      }
    }

    // Build CSV
    let csv = '\uFEFF'; // BOM for Excel UTF-8
    csv += '"' + t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum') + '";';
    csv += '"' + t('statistics.personal.addenda.myAddenda', 'Meine Addenda') + '"\n';

    filteredData.forEach(row => {
      csv += '="' + formatPeriod(row.period) + '";';
      csv += row.addendaCount + '\n';
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `addenda_statistik_${currentUser?.id}_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export Function
  const handlePdfDownload = async () => {
    if (!addendaData || !showAddenda || !chartRef.current) return;

    try {
      // Farben dynamisch aus CSS-Variablen auslesen
      const rootStyles = getComputedStyle(document.documentElement);
      const chartColor1 = rootStyles.getPropertyValue('--rd-chart-1').trim();

      const chartElement = chartRef.current;
      const canvas = await html2canvas(chartElement, {
        scale: 2,
        backgroundColor: 'var(--rd-bg-paper)'
      });
      const chartImage = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;

      const getChartDataForPeriod = () => {
        // Wähle die richtigen Daten basierend auf dem timeFilter
        let data = [];
        switch (timeFilter) {
          case 'date':
            data = addendaData.by_date || [];
            break;
          case 'week':
            data = addendaData.by_week || [];
            break;
          case 'month':
            data = addendaData.by_month || [];
            break;
          case 'quarter':
            data = addendaData.by_quarter || [];
            break;
          case 'year':
            data = addendaData.by_year || [];
            break;
          default:
            data = addendaData.by_month || [];
        }

        // Konvertiere zu einheitlichem Format
        const mergedData = data.map(item => ({
          period: item.period,
          addendaCount: item.count || 0
        }));

        // Filtere nach startPeriod und endPeriod
        let filteredData = mergedData;
        if (startPeriod && endPeriod) {
          const startIdx = mergedData.findIndex(d => d.period === startPeriod);
          const endIdx = mergedData.findIndex(d => d.period === endPeriod);
          if (startIdx !== -1 && endIdx !== -1) {
            filteredData = mergedData.slice(startIdx, endIdx + 1);
          }
        } else if (startPeriod) {
          const startIdx = mergedData.findIndex(d => d.period === startPeriod);
          if (startIdx !== -1) {
            filteredData = mergedData.slice(startIdx);
          }
        } else if (endPeriod) {
          const endIdx = mergedData.findIndex(d => d.period === endPeriod);
          if (endIdx !== -1) {
            filteredData = mergedData.slice(0, endIdx + 1);
          }
        }

        return filteredData;
      };

      const chartData = getChartDataForPeriod();

      const formatPeriod = (period) => {
        if (!period) return '';
        try {
          switch (timeFilter) {
            case 'date':
              if (!isFormatConfigLoaded) return period;
              return formatDateSync(period);
            case 'week':
              const weekMatch = period.match(/(\d{4})-W(\d{2})/);
              if (weekMatch) return `KW${weekMatch[2]} - ${weekMatch[1]}`;
              return period;
            case 'month':
              const monthMatch = period.match(/(\d{4})-(\d{2})/);
              if (monthMatch) return `${monthMatch[2]}.${monthMatch[1]}`;
              return period;
            case 'quarter':
              const quarterMatch = period.match(/(\d{4})-Q(\d)/);
              if (quarterMatch) return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
              return period;
            case 'year':
              return period;
            default:
              return period;
          }
        } catch (error) {
          return period;
        }
      };

      const actualStartPeriod = startPeriod || (chartData.length > 0 ? chartData[0].period : '');
      const actualEndPeriod = endPeriod || (chartData.length > 0 ? chartData[chartData.length - 1].period : '');
      
      const addFooter = () => {
        const footerY = pageHeight - 10;
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        const footerText = `Addenda-Bericht, ${currentUser?.title || ''} ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() + 
                          `, Zeitraum ${formatPeriod(actualStartPeriod)} - ${formatPeriod(actualEndPeriod)}`;
        pdf.text(footerText, 20, footerY);
        pdf.setTextColor(0, 0, 0);
      };

      pdf.setFontSize(18);
      pdf.text(t('statistics.personal.pdf.title', 'Befund-Report'), 20, yPosition);
      yPosition += 15;

      pdf.setFontSize(12);
      const userName = `${currentUser?.title || ''} ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim();
      pdf.text(`${t('statistics.personal.pdf.reportingPhysician', 'Befunder')}: ${userName}`, 20, yPosition);
      yPosition += 7;

      const timeFilterLabel = {
        date: t('statistics.personal.fallzahlen.day', 'Tag'),
        week: t('statistics.personal.fallzahlen.week', 'Woche'),
        month: t('statistics.personal.fallzahlen.month', 'Monat'),
        quarter: t('statistics.personal.fallzahlen.quarter', 'Quartal'),
        year: t('statistics.personal.fallzahlen.year', 'Jahr')
      }[timeFilter] || timeFilter;
      
      pdf.text(`${t('statistics.personal.pdf.interval', 'Intervall')}: ${timeFilterLabel}`, 20, yPosition);
      yPosition += 7;
      
      const periodText = `${t('statistics.personal.pdf.period', 'Zeitraum')}: ${formatPeriod(actualStartPeriod)} - ${formatPeriod(actualEndPeriod)}`;
      pdf.text(periodText, 20, yPosition);
      yPosition += 15;

      const chartWidth = 120;
      const chartHeight = (canvas.height * chartWidth) / canvas.width;
      const chartY = yPosition;
      pdf.addImage(chartImage, 'PNG', 20, yPosition, chartWidth, chartHeight);

      const legendX = 20 + chartWidth + 10;
      let legendY = chartY + 5;
      
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text(t('statistics.personal.pdf.legend', 'Legende') + ':', legendX, legendY);
      pdf.setFont('helvetica', 'normal');
      legendY += 4;

      // Nur eine Legende für Addenda
      pdf.setFillColor(chartColor1);
      pdf.rect(legendX, legendY - 2, 2, 2, 'F');
      pdf.setFontSize(6);
      pdf.text(t('statistics.personal.addenda.myAddenda', 'Meine Addenda'), legendX + 4, legendY);
      legendY += 4;

      yPosition = chartY + chartHeight + 10;

      // Tabellen-Header und -Daten für Addenda
      const tableHeaders = [
        t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum'),
        t('statistics.personal.addenda.myAddenda', 'Meine Addenda')
      ];

      const tableData = chartData.map(row => [
        formatPeriod(row.period),
        row.addendaCount
      ]);

      pdf.autoTable({
        head: [tableHeaders],
        body: tableData,
        startY: yPosition,
        margin: { left: 20, right: 20, bottom: 20 },
        styles: { 
          fontSize: 8,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        bodyStyles: {
          fillColor: [255, 255, 255]
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255]
        },
        didDrawPage: function(data) {
          addFooter();
        }
      });

      addFooter();

      const signatureLineHeight = 5 + 10;
      yPosition = pdf.lastAutoTable.finalY + 60;
      
      if (yPosition + signatureLineHeight > pageHeight - 20) {
        pdf.addPage();
        addFooter();
        yPosition = 50;
      }
      
      const signatureLineLength = 60;
      pdf.setFontSize(10);
      pdf.line(20, yPosition, 20 + signatureLineLength, yPosition);
      yPosition += 5;
      pdf.text(t('statistics.personal.pdf.signature', 'Unterschrift und Stempel'), 20, yPosition);
      yPosition += 15;

      const now = new Date();
      const generationDate = isFormatConfigLoaded ? formatDateSync(now.toISOString().split('T')[0]) : now.toLocaleDateString('de-DE');
      const generationTime = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      const electronicText = t('statistics.personal.pdf.electronicallyGenerated', 
        'Der Bericht wurde elektronisch erzeugt durch {{title}} {{name}} am {{date}} um {{time}}.',
        { 
          title: currentUser?.title || '',
          name: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim(),
          date: generationDate,
          time: generationTime
        }
      );
      pdf.text(electronicText, 20, yPosition);

      pdf.save('addenda_report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError(t('statistics.personal.pdf.error', 'Fehler beim Erstellen der PDF'), 'ERR-PDF-001');
    }
  };

  if (addendaLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!addendaData) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('statistics.personal.addenda.noData', 'Keine Daten verfügbar')}
        </Typography>
      </Box>
    );
  }

  const getChartData = () => {
    // Wähle die richtigen Daten basierend auf dem timeFilter
    let data = [];
    switch (timeFilter) {
      case 'date':
        data = addendaData.by_date || [];
        break;
      case 'week':
        data = addendaData.by_week || [];
        break;
      case 'month':
        data = addendaData.by_month || [];
        break;
      case 'quarter':
        data = addendaData.by_quarter || [];
        break;
      case 'year':
        data = addendaData.by_year || [];
        break;
      default:
        data = addendaData.by_month || [];
    }

    // Konvertiere zu einheitlichem Format
    const completeData = data.map(item => ({
      period: item.period,
      addendaCount: item.count || 0
    }));

    // Filtere nach startPeriod und endPeriod
    let filteredData = completeData;
    if (startPeriod && endPeriod) {
      const startIndex = completeData.findIndex(item => item.period === startPeriod);
      const endIndex = completeData.findIndex(item => item.period === endPeriod);
      
      if (startIndex !== -1 && endIndex !== -1 && startIndex <= endIndex) {
        filteredData = completeData.slice(startIndex, endIndex + 1);
      }
    } else if (startPeriod) {
      const startIndex = completeData.findIndex(item => item.period === startPeriod);
      if (startIndex !== -1) {
        filteredData = completeData.slice(startIndex);
      }
    } else if (endPeriod) {
      const endIndex = completeData.findIndex(item => item.period === endPeriod);
      if (endIndex !== -1) {
        filteredData = completeData.slice(0, endIndex + 1);
      }
    }

    return filteredData;
  };

  const chartData = getChartData();
  
  const availablePeriods = (() => {
    let data = [];
    switch (timeFilter) {
      case 'date':
        data = addendaData.by_date || [];
        break;
      case 'week':
        data = addendaData.by_week || [];
        break;
      case 'month':
        data = addendaData.by_month || [];
        break;
      case 'quarter':
        data = addendaData.by_quarter || [];
        break;
      case 'year':
        data = addendaData.by_year || [];
        break;
      default:
        data = addendaData.by_month || [];
    }
    
    return data.map(d => d.period).sort().reverse();
  })();

  const formatPeriod = (period) => {
    if (!period) return period;
    
    try {
      switch (timeFilter) {
        case 'date':
          if (!isFormatConfigLoaded) return period;
          return formatDateSync(period);
        
        case 'week':
          const weekMatch = period.match(/(\d{4})-W(\d{2})/);
          if (weekMatch) {
            return `KW${weekMatch[2]} - ${weekMatch[1]}`;
          }
          return period;
        
        case 'month':
          const monthMatch = period.match(/(\d{4})-(\d{2})/);
          if (monthMatch) {
            return `${monthMatch[2]}.${monthMatch[1]}`;
          }
          return period;
        
        case 'quarter':
          const quarterMatch = period.match(/(\d{4})-Q(\d)/);
          if (quarterMatch) {
            return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
          }
          return period;
        
        case 'year':
          return period;
        
        default:
          return period;
      }
    } catch (error) {
      console.error('Error formatting period:', error);
      return period;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('statistics.personal.categories.addenda', 'Addenda')}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, mb: 3, flexWrap: 'nowrap' }}>
        <Grid container spacing={2} sx={{ flex: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'var(--rd-chart-1)', color: 'white', height: '100%', width: '200px' }}>
              <CardContent>
                <Typography variant="h4" align="center">
                  {addendaData.total_count}
                </Typography>
                <Typography variant="body2" align="center">
                  {t('statistics.personal.addenda.myAddenda', 'Meine Addenda')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
          <FormControl sx={{ width: 120 }}>
            <InputLabel>{t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum')}</InputLabel>
            <Select
              value={timeFilter}
              onChange={handleTimeFilterChange}
              label={t('statistics.personal.fallzahlen.timePeriod', 'Zeitraum')}
            >
              <MenuItem value="date">{t('statistics.personal.fallzahlen.day', 'Tag')}</MenuItem>
              <MenuItem value="week">{t('statistics.personal.fallzahlen.week', 'Woche')}</MenuItem>
              <MenuItem value="month">{t('statistics.personal.fallzahlen.month', 'Monat')}</MenuItem>
              <MenuItem value="quarter">{t('statistics.personal.fallzahlen.quarter', 'Quartal')}</MenuItem>
              <MenuItem value="year">{t('statistics.personal.fallzahlen.year', 'Jahr')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120 }}>
            <InputLabel>{t('statistics.personal.fallzahlen.startPeriod', 'Von')}</InputLabel>
            <Select
              value={startPeriod}
              onChange={handleStartPeriodChange}
              label={t('statistics.personal.fallzahlen.startPeriod', 'Von')}
            >
              <MenuItem value="">{t('statistics.personal.fallzahlen.all', 'Alle')}</MenuItem>
              {availablePeriods.map((period) => (
                <MenuItem key={period} value={period}>
                  {formatPeriod(period)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120 }}>
            <InputLabel>{t('statistics.personal.fallzahlen.endPeriod', 'Bis')}</InputLabel>
            <Select
              value={endPeriod}
              onChange={handleEndPeriodChange}
              label={t('statistics.personal.fallzahlen.endPeriod', 'Bis')}
            >
              <MenuItem value="">{t('statistics.personal.fallzahlen.all', 'Alle')}</MenuItem>
              {availablePeriods.map((period) => (
                <MenuItem key={period} value={period}>
                  {formatPeriod(period)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t('statistics.personal.addenda.chartTitle', 'Entwicklung der Addenda')}
          {' '}
          {timeFilter === 'date' && t('statistics.personal.fallzahlen.perDay', 'pro Tag')}
          {timeFilter === 'week' && t('statistics.personal.fallzahlen.perWeek', 'pro Woche')}
          {timeFilter === 'month' && t('statistics.personal.fallzahlen.perMonth', 'pro Monat')}
          {timeFilter === 'quarter' && t('statistics.personal.fallzahlen.perQuarter', 'pro Quartal')}
          {timeFilter === 'year' && t('statistics.personal.fallzahlen.perYear', 'pro Jahr')}
        </Typography>
        
        {!showAddenda ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.personal.fallzahlen.noSeriesSelected', 'Bitte wählen Sie mindestens eine Kategorie aus')}
          </Typography>
        ) : chartData.length > 0 ? (
          <Box ref={chartRef} sx={{ width: '100%', height: 400 }}>
            <LineChart
              dataset={chartData}
              xAxis={[{ 
                dataKey: 'period',
                scaleType: 'point',
                tickLabelInterval: chartData.length > 30 ? Math.floor(chartData.length / 20) : 0,
                valueFormatter: (value) => formatPeriod(value)
              }]}
              yAxis={[{
                min: 0,
                max: (() => {
                  // Finde den maximalen Wert
                  const maxValue = Math.max(...chartData.map(d => d.addendaCount));
                  // Füge 10% Puffer hinzu
                  return Math.ceil(maxValue * 1.1);
                })()
              }]}
              series={[
                ...(showAddenda ? [{
                  type: 'line',
                  dataKey: 'addendaCount',
                  label: t('statistics.personal.addenda.myAddenda', 'Meine Addenda'),
                  color: 'var(--rd-chart-1)',
                  curve: 'natural',
                  area: true,
                  showMark: false,
                  valueFormatter: (value) => `${value}`
                }] : [])
              ]}
              height={450}
              margin={{ top: 20, right: 30, bottom: 80, left: 70 }}
              grid={{ vertical: false, horizontal: true }}
              tooltip={{
                trigger: 'axis'
              }}
              sx={{
                '.MuiLineElement-root': {
                  strokeWidth: 2.5
                },
                '.MuiAreaElement-root': {
                  fillOpacity: 0.3
                },
                '.MuiMarkElement-root': {
                  scale: '0.9',
                  fill: 'var(--rd-primary-contrast)',
                  strokeWidth: 2
                },
                '.MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                  transform: 'translateY(10px) rotate(-45deg)',
                  textAnchor: 'end !important'
                },
                '.MuiChartsLegend-root': {
                  display: 'none !important'
                }
              }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.personal.fallzahlen.noChartData', 'Keine Daten für die ausgewählten Filter verfügbar')}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={showAddenda} 
                onChange={(e) => setShowAddenda(e.target.checked)}
                sx={{ color: 'var(--rd-chart-1)', '&.Mui-checked': { color: 'var(--rd-chart-1)' } }}
              />
            }
            label={t('statistics.personal.addenda.myAddenda', 'Meine Addenda')}
          />
        </Box>
      </Paper>
    </Box>
  );
});

Addenda.displayName = 'Addenda';

export default Addenda;
